import { Component, OnInit, HostListener, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { TitleService } from '@core/services/title.service';
import { WindowService } from '@core/services/window.service';
import { KeyPressService } from '@app/services/app/keypress.service';
import { Router } from '@angular/router';
import { InterfaceListenerService } from '@core/services/interface-listener/interface-listener.service';

import { environment } from '@environment/environment';

import { AppService } from '@app/services/app/app.service';
import { AppBehaviorService } from 'app/services/app/app-behavior.service';

import { AccountSession } from '@core/objects/account-session';
import * as Sentry from "@sentry/angular-ivy";

@Component({
    selector: 'app-main',
    templateUrl: './main.component.html',
    styleUrls: ['./main.component.scss']
})
export class AppMainComponent implements OnInit, OnDestroy {

    public session: any = null;
    public accountSession: AccountSession = null;
    public logged: boolean = true;
    public disconnected: boolean = false;

    public accessChecked: boolean = false;
    public accessDenied: boolean = false;


    private subs = new Subscription();

    constructor(
        private app: AppService,
        private behavior: AppBehaviorService,
        private window: WindowService,
        protected title: TitleService,
        private router: Router,
        private keyPressSrv: KeyPressService,
        private iListenerSrv: InterfaceListenerService) {

        this.title.setPrefix("Twona");
    }

    @HostListener('window:blur', ['$event'])
    onBlur(event: any): void {
        this.keyPressSrv.distributeKeyPressed(null); // clean keydown and keypressed on leave page
    }

    @HostListener('document:keydown', ['$event'])
    handleKeydown(event: KeyboardEvent) {
        // event.keyCode is deprecated
        this.keyPressSrv.distributeKeyPressed(event);
    }
    @HostListener('document:keyup', ['$event'])
    handleKeyup(event: KeyboardEvent): void {
        // console.log(" handle keyup ",event);
        this.keyPressSrv.distributeKeyPressed(event);
    }

    @HostListener('document:click', ['$event'])
    documentClick(event: PointerEvent) {
        // trigger the event on the interface listener service
        this.setDocumentClick(event)
    }

    ngOnInit(){
        this.behavior.start();

        this.subs.add(this.app.getSession().subscribe( (session: any) => {
            this.session = session;

            if (session == null)
                return;
        }));


        this.subs.add(this.app.getAccountSession().subscribe((accSession: AccountSession) => {
            this.accountSession = accSession;
            if (accSession == null)
                return;

            // Set user and org data for sentry
            if (accSession.current?.user?.email)
                Sentry.setUser({ email: accSession.current.user.email });
            if (accSession.current?.organization?.id)
                Sentry.setTag("organization", accSession.current.organization.id);


            // wootric (before sep 2026 the user created_at field was undefined and it still worked wihtout reason)
            this.window.nativeWindow['wootricSettings'] = {
                email: accSession.current.user.email, // Required to uniquely identify a user. Email is recommended but this can be any unique identifier.
                created_at: new Date(accSession.current.user.created_at).getTime() / 1000,
                account_token: 'NPS-d8cba788' // This is your unique account token.
            };
            this.window.nativeWindow['wootric']('run');
        }));

        this.subs.add(this.app.getAppAccess().subscribe((hasAccess: boolean | null) => {
            if (hasAccess == null)
                return; // accounts session hasn't resolved yet

            this.accessChecked = true;
            this.accessDenied = !hasAccess;
        }));

        this.subs.add(this.app.getHttpError().subscribe(
            (error:number) => {
                switch(error) {
                    case AppService.HTTP_ERROR_NOT_LOGGED:
                        if(this.session == null)
                            window.location.href = environment.loginBaseUri + '?authReason=nologin&href='+encodeURIComponent(window.location.href);
                        else
                            this.logged = false;

                        break;
                    case AppService.HTTP_ERROR_TOO_MANY_CONNECTIONS:
                        window.location.href = environment.loginBaseUri + '/sessions?href='+encodeURIComponent(window.location.href);
                        break;
                }
            }
        ));
    }

    ngOnDestroy(): void {
        this.behavior.end();
        this.subs.unsubscribe();
    }

    setDocumentClick(event: PointerEvent){
        this.iListenerSrv.setDocumentClick(event);
    }

    /**
     * Get the layout classes based on the current route.
     * @returns An object with class names as keys and boolean values indicating whether to apply them.
     */
    getLayoutClasses(){
        if (this.router.url.includes('/library') || this.router.url.startsWith('/versions') )
            return {'with-overlay': !this.logged,'library-bg':true};
        if (this.router.url.startsWith('/settings'))
            return {'with-overlay': !this.logged,'settings-bg':true};
        if (this.router.url.startsWith('/projects/add'))
            return {'with-overlay': !this.logged,'add-project-bg':true};
        if (this.router.url.startsWith('/ai'))
            return { 'with-overlay': !this.logged, 'ai-bg': true };
        return {'with-overlay': !this.logged,'dashboard-bg':true};
    }
}
